import type { SelectOption, SelectOptionsOrGroups } from '@ardoq/select';
import { t, type I18nLocale } from '@ardoq/i18n';
import { matchSelectedReferenceType } from '@ardoq/settings-bar';
import {
  ComponentLabelSource,
  COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
} from '@ardoq/data-model';
import type { SourceSelectComponents } from './ComponentMatrix2AxisDropdownSelectComponents';
import {
  getAxisSourceKey,
  isFieldSourceValue,
  isParentSourceValue,
  isRefSourceValue,
} from './utils';
import type {
  AdditionalFieldsSelectProps,
  AxisDropdownTexts,
  SortingSelectProps,
  SourceSelectProps,
  AxisHeaderInputProps,
  ComponentMatrix2AxisDropdownContentViewProps,
} from './ComponentMatrix2AxisDropdownContentView';
import { AxisSortOrder, AxisSourceOption, AxisSourceValue } from './types';
import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';

export type DropdownLocalState = {
  isSourceMenuOpen: boolean;
  setIsSourceMenuOpen: (isOpen: boolean) => void;
  sourceSelectComponents: SourceSelectComponents;
};

export const areAxisSourceValuesEqual = (
  a: AxisSourceValue,
  b: AxisSourceValue
): boolean => {
  if (a.type !== b.type) return false;

  if (isFieldSourceValue(a) && isFieldSourceValue(b)) {
    return a.fieldName === b.fieldName;
  }

  if (isRefSourceValue(a) && isRefSourceValue(b)) {
    return matchSelectedReferenceType(a.ref, b.ref);
  }

  return isParentSourceValue(a) && isParentSourceValue(b);
};

const filterSourceOption = (
  option: { data: AxisSourceOption },
  inputValue: string
): boolean => {
  if (!inputValue) return true;
  const search = inputValue.toLowerCase();
  const label = option.data.label.toLowerCase();
  const description = (option.data.description ?? '').toLowerCase();
  return label.includes(search) || description.includes(search);
};

export const flattenOptionKeys = (
  options: SelectOptionsOrGroups<AxisSourceValue>
): string[] =>
  options.flatMap(option =>
    'options' in option
      ? option.options.map(child => getAxisSourceKey(child.value))
      : [getAxisSourceKey(option.value)]
  );

const getSelectedSourceLabel = (
  sourceValue: AxisSourceValue | null,
  sourceOptions: SelectOptionsOrGroups<AxisSourceValue>
): string | null => {
  if (sourceValue === null) return null;

  const flatOptions = sourceOptions.flatMap(option =>
    'options' in option ? option.options : [option]
  );

  return (
    flatOptions.find(option =>
      areAxisSourceValuesEqual(option.value, sourceValue)
    )?.label ?? null
  );
};

const toSourceSelectProps = ({
  sourceValue,
  sourceOptions,
  onSourceChange,
  isSourceMenuOpen,
  setIsSourceMenuOpen,
  i18nLocale,
  sourceSelectComponents,
}: {
  sourceValue: AxisSourceValue | null;
  sourceOptions: SelectOptionsOrGroups<AxisSourceValue>;
  onSourceChange: (value: AxisSourceValue | null) => void;
  isSourceMenuOpen: boolean;
  setIsSourceMenuOpen: (isOpen: boolean) => void;
  i18nLocale: I18nLocale;
  sourceSelectComponents: SourceSelectComponents;
}): SourceSelectProps => ({
  'aria-label': t(
    i18nLocale,
    'tabview:componentMatrix2.axis.fieldOrReference',
    'Field or reference'
  ),
  dataTestId: 'component-matrix-axis-source-select',
  placeholder: t(
    i18nLocale,
    'tabview:componentMatrix2.axis.selectFieldOrReference',
    'Select field or reference'
  ),
  value: sourceOptions
    .flatMap(option => ('options' in option ? option.options : [option]))
    .find(
      option =>
        sourceValue !== null &&
        areAxisSourceValuesEqual(option.value, sourceValue)
    ),
  options: sourceOptions,
  getOptionValue: (option: AxisSourceOption) => getAxisSourceKey(option.value),
  onValueChange: (value: AxisSourceValue | null) => {
    if (
      value !== null &&
      sourceValue !== null &&
      areAxisSourceValuesEqual(value, sourceValue)
    ) {
      onSourceChange(null);
      setIsSourceMenuOpen(false);
      return;
    }

    onSourceChange(value);
  },
  filterOption: filterSourceOption,
  menuIsOpen: isSourceMenuOpen,
  onMenuOpen: () => setIsSourceMenuOpen(true),
  onMenuClose: () => setIsSourceMenuOpen(false),
  components: sourceSelectComponents,
  autoFocus: true,
});

export const toAxisHeaderInputProps = ({
  axisHeaderLabel,
  selectedSourceLabel,
  value,
  onValueChange,
  isDisabled,
}: {
  axisHeaderLabel: string;
  selectedSourceLabel: string | null;
  value: string | null;
  onValueChange: (value: string | null) => void;
  isDisabled: boolean;
}): AxisHeaderInputProps => ({
  inputProps: {
    'aria-label': axisHeaderLabel,
    dataTestId: 'component-matrix-axis-header-input',
    placeholder: selectedSourceLabel ?? '',
    isDisabled,
  },
  persistedValue: isDisabled ? null : value,
  defaultValue: selectedSourceLabel ?? '',
  onPersist: onValueChange,
});

const toSortingSelectProps = ({
  sortingValue,
  sortingOptions,
  onSortingChange,
  isSortingDisabled,
  i18nLocale,
}: {
  sortingValue: AxisSortOrder | null;
  sortingOptions: SelectOption<AxisSortOrder>[];
  onSortingChange: (value: AxisSortOrder) => void;
  isSortingDisabled: boolean;
  i18nLocale: I18nLocale;
}): SortingSelectProps => ({
  'aria-label': t(
    i18nLocale,
    'tabview:componentMatrix2.axis.sorting',
    'Sorting'
  ),
  dataTestId: 'component-matrix-axis-sorting-select',
  placeholder: t(
    i18nLocale,
    'tabview:componentMatrix2.axis.sortingDefault',
    'Default'
  ),
  value: isSortingDisabled ? undefined : (sortingValue ?? undefined),
  options: sortingOptions,
  onValueChange: (value: AxisSortOrder | null) => {
    if (value === null) return;
    onSortingChange(value);
  },
  isDisabled: isSortingDisabled,
});

const toAxisDropdownTexts = ({
  i18nLocale,
  isHeaderDisabled,
  isSortingDisabled,
}: {
  i18nLocale: I18nLocale;
  isHeaderDisabled: boolean;
  isSortingDisabled: boolean;
}): AxisDropdownTexts => {
  const axisHeaderLabel = t(
    i18nLocale,
    'tabview:componentMatrix2.axis.columnHeader',
    'Header label'
  );

  const axisPopoverContent = t(
    i18nLocale,
    'tabview:componentMatrix2.axis.popover',
    'Select a field or reference first.'
  );

  return {
    sortingLabel: t(
      i18nLocale,
      'tabview:componentMatrix2.axis.sortBy',
      'Sort by (optional)'
    ),
    sortingPopoverContent: isSortingDisabled ? axisPopoverContent : null,
    axisHeaderLabel,
    axisHeaderPopoverContent: isHeaderDisabled ? axisPopoverContent : null,
    axisHeaderHelperText: t(
      i18nLocale,
      'tabview:componentMatrix2.axis.columnHeader.helper',
      'Shown instead of the field or reference name.'
    ),
    additionalFieldsLabel: t(
      i18nLocale,
      'tabview:componentMatrix2.axis.additionalFields',
      'Add fields to header'
    ),
  };
};

export const toComponentMatrix2AxisDropdownContentViewProps = (
  {
    value,
    onValueChange,
    label,
    sourceValue,
    sourceOptions,
    onSourceChange,
    sortingValue,
    isSortingDisabled,
    sortingOptions,
    onSortingChange,
    i18nLocale,
    additionalHeaderFields,
    additionalHeaderFieldNames,
    onAdditionalHeaderFieldNamesChange,
  }: {
    label: string;
    value: string | null;
    onValueChange: (value: string | null) => void;
    sourceValue: AxisSourceValue | null;
    sourceOptions: SelectOptionsOrGroups<AxisSourceValue>;
    onSourceChange: (value: AxisSourceValue | null) => void;
    sortingValue: AxisSortOrder | null;
    isSortingDisabled: boolean;
    sortingOptions: SelectOption<AxisSortOrder>[];
    onSortingChange: (value: AxisSortOrder) => void;
    i18nLocale: I18nLocale;
    additionalHeaderFields: FieldWithPermissions[];
    additionalHeaderFieldNames: string[];
    onAdditionalHeaderFieldNamesChange: (fieldNames: string[]) => void;
  },
  localState: DropdownLocalState
): ComponentMatrix2AxisDropdownContentViewProps => {
  const selectedSourceLabel = getSelectedSourceLabel(
    sourceValue,
    sourceOptions
  );
  const isHeaderDisabled = sourceValue === null;

  const texts = toAxisDropdownTexts({
    i18nLocale,
    isHeaderDisabled,
    isSortingDisabled,
  });

  return {
    label,
    sourceSelect: toSourceSelectProps({
      sourceValue,
      sourceOptions,
      onSourceChange,
      i18nLocale,
      isSourceMenuOpen: localState.isSourceMenuOpen,
      setIsSourceMenuOpen: localState.setIsSourceMenuOpen,
      sourceSelectComponents: localState.sourceSelectComponents,
    }),
    axisHeaderInput: toAxisHeaderInputProps({
      axisHeaderLabel: texts.axisHeaderLabel,
      selectedSourceLabel,
      value,
      onValueChange,
      isDisabled: isHeaderDisabled,
    }),
    sortingSelect: toSortingSelectProps({
      sortingValue,
      sortingOptions,
      onSortingChange,
      isSortingDisabled,
      i18nLocale,
    }),
    texts,
    additionalFieldsSelect:
      isHeaderDisabled || isFieldSourceValue(sourceValue)
        ? undefined
        : toAdditionalFieldsSelectProps({
            additionalHeaderFields,
            additionalHeaderFieldNames,
            onAdditionalHeaderFieldNamesChange,
            i18nLocale,
          }),
  };
};

const toAdditionalFieldsSelectProps = ({
  additionalHeaderFields,
  additionalHeaderFieldNames,
  onAdditionalHeaderFieldNamesChange,
  i18nLocale,
}: {
  additionalHeaderFields: FieldWithPermissions[];
  additionalHeaderFieldNames: string[];
  onAdditionalHeaderFieldNamesChange: (fieldNames: string[]) => void;
  i18nLocale: I18nLocale;
}): AdditionalFieldsSelectProps => {
  const defaultFieldOptions: SelectOption<string>[] = [
    {
      label: t(
        i18nLocale,
        'tabview:componentMatrix2.axis.additionalFields.componentType',
        'Component type'
      ),
      value: ComponentLabelSource.TYPE,
    },
    {
      label: t(
        i18nLocale,
        'tabview:componentMatrix2.axis.additionalFields.description',
        'Description'
      ),
      value: COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
    },
  ];
  const customOptions: SelectOption<string>[] = additionalHeaderFields.map(
    field => ({ value: field.name, label: field.label ?? field.name })
  );
  const options: SelectOptionsOrGroups<string> = [
    {
      label: t(
        i18nLocale,
        'tabview:componentMatrix2.axis.additionalFields.defaultFields',
        'Default fields'
      ),
      options: defaultFieldOptions,
    },
    ...(customOptions.length > 0
      ? [
          {
            label: t(
              i18nLocale,
              'tabview:componentMatrix2.axis.additionalFields.customFields',
              'Custom fields'
            ),
            options: customOptions,
          },
        ]
      : []),
  ];
  const allOptions = [...defaultFieldOptions, ...customOptions];
  const value = allOptions.filter(opt =>
    additionalHeaderFieldNames.includes(opt.value)
  );
  return {
    'aria-label': t(
      i18nLocale,
      'tabview:componentMatrix2.axis.additionalFields.ariaLabel',
      'Add fields to header'
    ),
    placeholder: t(
      i18nLocale,
      'tabview:componentMatrix2.axis.additionalFields.placeholder',
      'Select fields...'
    ),
    options,
    value,
    onValueChange: (names: string[] | null) =>
      onAdditionalHeaderFieldNamesChange(names ?? []),
  };
};
